import React, { useState, useEffect } from 'react';
import HolsterVisualizer from '../HolsterVisualizer';
import ColorSelector from '../ColorSelector';
import OptionSelector from '../OptionSelector';
import SegmentedControl from '../SegmentedControl';
import useConfigShare from '../useConfigShare';
import ShareModal from '../ShareModal';

// --- NEW IMPORTS FOR REUSABLE COMPONENTS ---
import DisclaimerBox from '../DisclaimerBox'; 
import CollapsibleSection from '../CollapsibleSection';

// FIX: Import getImagePath for correct path resolution on WordPress
import { getImagePath } from '../config/imagePath';

// 1. Import Global Settings (Removed RUSH imports)
import { 
  GLOBAL_DISCOUNT_PERCENT, 
  SALE_NAME,
  // Lead time labels + colors
  CUSTOM_LABEL, CUSTOM_COLOR, leadTimeTint,
  optionLine,
} from '../config/globalSettings';

// 2. Import Data
import { nidhoggLvl2Data } from '../data/nidhoggLvl2Data'; 
import '../App.css';

function NidhoggLvl2Builder() {
  // --- STATE ---
  const [viewSide, setViewSide] = useState("front"); 
  const [gunId, setGunId] = useState("");
  const [lightId, setLightId] = useState("");
  const [handId, setHandId] = useState("right"); 
  const [colorId, setColorId] = useState("");
  const [mountId, setMountId] = useState("");
  
  // SUB-OPTIONS STATE
  const [tacBeltColor, setTacBeltColor] = useState("");
  const [tacBeltSize, setTacBeltSize] = useState("");
  const [fulcrumColor, setFulcrumColor] = useState("");
  const [thighBandColor, setThighBandColor] = useState("");

  // MAG CARRIER ADDON STATE
  const [magId, setMagId] = useState("");
  const [magMountId, setMagMountId] = useState("");

  const [breakthroughId, setBreakthroughId] = useState("");

  // Rush state remains for config tracking but is not used in UI for Lvl 2
  const [rushService, setRushService] = useState(false);

  // --- SHARE FUNCTIONALITY ---
  const {
    getConfigFromURL, generateShareableURL, saveToLocalStorage, loadFromLocalStorage,
    savedConfigs, saveNamedConfig, deleteNamedConfig, copyToClipboard, copySuccess,
    shareModalOpen, setShareModalOpen,
  } = useConfigShare();

  const getCurrentConfig = () => ({
    gunId, lightId, handId, colorId, mountId, 
    tacBeltColor, tacBeltSize, fulcrumColor, thighBandColor,
    magId, magMountId, 
    breakthroughId
  });

  const loadConfig = (incomingData) => {
    if (!incomingData || incomingData.type === 'click' || incomingData._reactName) return;

    try {
        const config = incomingData.config ? incomingData.config : incomingData;

        setGunId(config.gunId || '');
        setLightId(config.lightId || '');
        setHandId(config.handId || 'right');
        setColorId(config.colorId || '');
        setMountId(config.mountId || '');
        
        setTacBeltColor(config.tacBeltColor || '');
        setTacBeltSize(config.tacBeltSize || '');
        setFulcrumColor(config.fulcrumColor || '');
        setThighBandColor(config.thighBandColor || '');
        
        setMagId(config.magId || '');
        setMagMountId(config.magMountId || '');

        setBreakthroughId(config.breakthroughId || '');

        setShareModalOpen(false);
    } catch (error) {
        console.error("Error loading config", error);
    }
  };

  useEffect(() => {
    const urlConfig = getConfigFromURL();
    if (urlConfig) {
      loadConfig(urlConfig);
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    const savedConfig = loadFromLocalStorage();
    if (savedConfig) { loadConfig(savedConfig); }
  }, []);

  useEffect(() => {
    if (gunId) saveToLocalStorage(getCurrentConfig());
  }, [gunId, lightId, handId, colorId, mountId, tacBeltColor, tacBeltSize, fulcrumColor, thighBandColor, magId, magMountId, breakthroughId]);

  const shareableURL = generateShareableURL(getCurrentConfig());

  // --- SELECTIONS ---
  const selectedGun = nidhoggLvl2Data.guns.find(g => g.id === gunId);
  const selectedLight = nidhoggLvl2Data.lights.find(l => l.id === lightId);
  const selectedHand = nidhoggLvl2Data.hands.find(h => h.id === handId);
  const selectedColor = nidhoggLvl2Data.colors.find(c => c.id === colorId);
  const selectedMount = nidhoggLvl2Data.mounts.find(m => m.id === mountId);

  // SUB-OPTION SELECTIONS
  const selectedTacColor = nidhoggLvl2Data.tac_belt_colors?.find(c => c.id === tacBeltColor);
  const selectedTacSize = nidhoggLvl2Data.tac_belt_sizes?.find(s => s.id === tacBeltSize);
  const selectedFulcrum = nidhoggLvl2Data.fulcrum_colors?.find(c => c.id === fulcrumColor);
  const selectedThigh = nidhoggLvl2Data.thigh_band_colors?.find(t => t.id === thighBandColor);

  const selectedMag = nidhoggLvl2Data.mag_carriers?.find(m => m.id === magId);
  const selectedMagMount = nidhoggLvl2Data.mag_mounts?.find(m => m.id === magMountId);
  const selectedBreakthrough = nidhoggLvl2Data.breakthrough.find(b => b.id === breakthroughId);

  // --- CONDITIONAL LOGIC FOR MAG CARRIER MOUNT ---
  const showMagMount = magId !== "" && magId !== "no";

  useEffect(() => {
      if (!showMagMount) {
          setMagMountId("");
      }
  }, [showMagMount]);

  // --- DYNAMIC MAG MOUNT MAPPING FOR DROPDOWN UI ---
  const dynamicMagMounts = (nidhoggLvl2Data.mag_mounts || []).map(mount => ({
      ...mount,
      name: magId === "yes_2" ? `2x ${mount.name}` : mount.name,
      price: magId === "yes_2" ? mount.price * 2 : mount.price
  }));

  // --- RESET LOGIC FOR MAIN MOUNT ---
  useEffect(() => {
    // Both Tactical Belt and Droplegs need the Belt parameters
    const needsTacBeltOptions = mountId === 'tac_belt_bolted' || mountId === 'dropleg_qls' || mountId === 'dropleg_no_qls';
    if (!needsTacBeltOptions) {
        setTacBeltColor("");
        setTacBeltSize("");
    }
    
    // Only Droplegs need the Fulcrum & Thigh parameters
    const needsDroplegOptions = mountId === 'dropleg_qls' || mountId === 'dropleg_no_qls';
    if (!needsDroplegOptions) {
        setFulcrumColor("");
        setThighBandColor("");
    }
  }, [mountId]);

  const getSystemImages = () => {
    let images = [];

    if (mountId === 'dropleg_qls' || mountId === 'dropleg_no_qls') {
        const suffix = mountId === 'dropleg_no_qls' ? '_no_qls' : '';
        
        if (tacBeltColor) {
            images.push(getImagePath(`mounts/owb_systems/tacbelt_${tacBeltColor}${suffix}_front.png`));
        }
        if (fulcrumColor) {
            const activeThigh = thighBandColor || "black";
            images.push(getImagePath(`mounts/owb_systems/dropleg_${fulcrumColor}_${activeThigh}${suffix}_front.png`));
        }
    }

    return images;
  };

  const systemImageArray = getSystemImages();
  
  // RESTRICTED TO DROPLEGS ONLY
  const hasMountSystem = ['dropleg_qls', 'dropleg_no_qls'].includes(mountId);

  // --- FILTERING LOGIC ---
  const getFilteredLights = () => {
    if (!selectedGun) return nidhoggLvl2Data.lights;
    
    // 1. Physical constraint rules (Original logic)
    const tlr7Allowed = ["glock_17", "glock_19"].includes(selectedGun.id);
    const polymerWalthers = ["walther_pdp_4", "walther_pdp_45", "walther_pdp_5"];
    const shortSmiths = ["sw_mp_20_4", "sw_mp_20_425"];
    const vampireRestricted = [...polymerWalthers, ...shortSmiths].includes(selectedGun.id);

    // 2. Data file allowed arrays (New logic for Hand-Specific XVL2)
    let dataAllowedLights = selectedGun.supported_lights || [];
    if (handId === 'left' && selectedGun.supported_lights_lh) {
        dataAllowedLights = selectedGun.supported_lights_lh;
    }

    return nidhoggLvl2Data.lights.filter(light => {
        // If data arrays exist, restrict to them first (this hides XVL2 for LH or Non-Glocks)
        if (dataAllowedLights.length > 0 && !dataAllowedLights.includes(light.id)) return false;

        // Apply original physical constraint exceptions
        if (light.id === "tlr7" && !tlr7Allowed) return false;
        if ((light.id === "x300v_a" || light.id === "x300v_b") && vampireRestricted) return false;
        
        return true;
    });
  };
  const filteredLights = getFilteredLights();

  // Reset the light if it becomes invalid (e.g. they had XVL2 right-handed, and swapped to left-handed)
  useEffect(() => {
    if (lightId && !filteredLights.find(l => l.id === lightId)) { setLightId(""); }
  }, [gunId, filteredLights, lightId, handId]);

  const getFilteredHands = () => {
      if (!selectedGun) return nidhoggLvl2Data.hands;
      const isGlock = selectedGun.id.includes("glock");
      return isGlock ? nidhoggLvl2Data.hands : nidhoggLvl2Data.hands.filter(h => h.id === "right");
  };
  const filteredHands = getFilteredHands();

  useEffect(() => {
      if (handId === 'left' && selectedGun && !selectedGun.id.includes("glock")) { setHandId("right"); }
  }, [gunId, handId, selectedGun]);


  // --- LEAD TIME LOGIC (STRICTLY 2-3 WEEKS) ---
  const calculateLeadTime = () => {
    if (!selectedGun) return { text: "Select Options", date: "...", type: "PENDING" };
    
    // Level 2 is purely custom, no quick ship logic applies
    const targetDays = 15; // 15 business days = 3 weeks
    
    const date = new Date();
    let daysAdded = 0;
    while (daysAdded < targetDays) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0 && date.getDay() !== 6) daysAdded++;
    }
    const dateString = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    
    return { type: CUSTOM_LABEL, timeline: "2-3 Weeks", date: dateString, color: CUSTOM_COLOR, bg: leadTimeTint(CUSTOM_COLOR), isCustom: true };
  };
  
  const leadTimeInfo = calculateLeadTime();

  // --- PRICING ---
  const BASE_PRICE = 190.99; 
  
  let activeMagMountPrice = 0;
  if (showMagMount && selectedMagMount) {
      activeMagMountPrice = magId === "yes_2" ? (selectedMagMount.price * 2) : selectedMagMount.price;
  }

  const subtotal = 
    BASE_PRICE +
    (selectedColor?.price || 0) +
    (selectedMount?.price || 0) +
    (selectedTacColor?.price || 0) +
    (selectedTacSize?.price || 0) +
    (selectedFulcrum?.price || 0) +
    (selectedThigh?.price || 0) +
    (selectedMag?.price || 0) +
    activeMagMountPrice + 
    (selectedBreakthrough?.price || 0);

  const discountAmount = subtotal * GLOBAL_DISCOUNT_PERCENT;
  const total = (subtotal - discountAmount).toFixed(2);

  // --- VALIDATION LOGIC ---
  let subOptionsValid = true;
  if (mountId === 'tac_belt_bolted' || mountId === 'dropleg_qls' || mountId === 'dropleg_no_qls') {
      if (!tacBeltColor || !tacBeltSize) subOptionsValid = false;
  }
  if (mountId === 'dropleg_qls' || mountId === 'dropleg_no_qls') {
      if (!fulcrumColor || !thighBandColor) subOptionsValid = false;
  }

  let isReady = gunId && lightId && handId && colorId && mountId && magId && breakthroughId && subOptionsValid;

  if (showMagMount && !magMountId) {
      isReady = false;
  }

  // --- ADD TO CART ---
  const handleAddToCart = () => {
    if (!isReady) return;

    let systemInfo = null;
    if (mountId === 'tac_belt_bolted') {
        systemInfo = `Belt Options:\n   - Color: ${selectedTacColor?.name}\n   - Size: ${selectedTacSize?.name}`;
    }
    if (mountId === 'dropleg_qls' || mountId === 'dropleg_no_qls') {
        systemInfo = `Belt Options:\n   - Color: ${selectedTacColor?.name}\n   - Size: ${selectedTacSize?.name}\nDropleg Options:\n   - Fulcrum: ${selectedFulcrum?.name}\n   - Thigh Band: ${selectedThigh?.name}`;
    }

    let magDetails = null;
    if (showMagMount) {
        const mountNameStr = magId === "yes_2" ? `2x ${selectedMagMount?.name}` : selectedMagMount?.name;
        magDetails = `Mag Carrier: ${selectedMag?.name}\n   - Mag Mount: ${mountNameStr}`;
    } else {
        // Declined: leave it off the ticket entirely.
        magDetails = null;
    }

    const configuration = [
        `Model: ${selectedGun?.name}`,
        `Light: ${selectedLight?.name}`,
        `Hand: ${selectedHand?.name}`,
        `Color: ${selectedColor?.name}`,
        `Mount: ${selectedMount?.name}`,
        systemInfo,
        magDetails,
        optionLine("Breakthrough", selectedBreakthrough?.name),
        `Warranty: Lifetime Warranty Included`,
        `Lead Time: ${leadTimeInfo.timeline}`, // <-- Swapped confusing "Rush: No" for specific timeline
    ].filter(Boolean).join('\n');

    const productID = 283910; // <--- UPDATE TO YOUR LIVE NIDHOGG ID HERE
    
    const params = new URLSearchParams();
    params.append('add-to-cart', productID);
    params.append('quantity', '1');
    params.append('anr_config', configuration);
    params.append('anr_price', total);

    window.location.href = `/checkout/?${params.toString()}`;
  };

  const SectionHeader = ({ title, description, imageUrl }) => (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
        <h3 style={{ textTransform: "uppercase", margin: 0, fontSize: "14px", color: "#D5A678" }}>{title}</h3>
        {(description || imageUrl) && (
            <div className="tooltip-wrapper">
                <span className="info-icon">i</span>
                <div className="tooltip-content">
                    {imageUrl && (
                        <div style={{marginBottom: "10px"}}>
                            <img src={imageUrl} alt="Info" style={{width: "100%", borderRadius: "4px"}} />
                        </div>
                    )}
                    {description}
                </div>
            </div>
        )}
    </div>
  );

  // --- MOUNT INTERCEPTOR ---
  // Inject the selected color into the Tac Belt image path
  let visualizerMountData = selectedMount;
  if (selectedMount && selectedMount.id === 'tac_belt_bolted' && tacBeltColor) {
      visualizerMountData = {
          ...selectedMount,
          img_layer_mount: `/images/mounts/nidhogg_l2/tac_belt_bolted_${tacBeltColor}_front.png`,
          img_layer_mount_back: `/images/mounts/nidhogg_l2/tac_belt_bolted_${tacBeltColor}_back.png`
      };
  }

  return (
    <div className={`builder-container ${colorId ? "sticky-active" : ""}`}>
      <div className="controls-section">
        <h1 className="header-title">NIDHOGG™ II<br/><span style={{fontSize:"16px", color:"#D5A678"}}>ACTIVE RETENTION HOLSTER SYSTEM</span></h1>
        
        <div style={{ marginBottom: "30px", color: "#aaa", fontSize: "13px", lineHeight: "1.6" }}>
            <p style={{marginBottom: "10px"}}>
                <strong>The ANR Design Nidhogg™ II Active Retention Holster</strong> is a game-changer for competitive or tactical shooters who demand both speed and security. This innovative holster features a unique, patented light-specific locking active retention mechanism that delivers lightning-fast draws while maintaining positive retention.
            </p>
            <strong style={{color: "#D5A678"}}>Key Features:</strong>
            <ul style={{ paddingLeft: "20px", marginTop: "5px", listStyleType: "disc" }}>
                <li><strong>Active Retention:</strong> Provides an extra layer of security with a large push-button release for rapid deployment.</li>
                <li><strong>Light-Specific Locking:</strong> Currently available for Surefire X300 Ultra series lights, with more options coming soon.</li>
                <li><strong>Durable Construction:</strong> Unibody 0.125″ thick KYDEX® Thermoplastics construction for rugged reliability.</li>
                <li><strong>Open Bottomed Design:</strong> Allows water and debris to clear easily.</li>
            </ul>
            <p style={{fontStyle: "italic", fontSize: "12px", marginTop: "5px"}}>See more details at the bottom of the page.</p>
        </div>

        {/* INPUTS */}
        <OptionSelector label="Select Weapon Model *" options={nidhoggLvl2Data.guns} selectedValue={gunId} onChange={setGunId} />
        
        <h3 style={{ textTransform: "uppercase", marginTop: "30px" }}>Draw Hand</h3>
        <OptionSelector label="Select Draw Hand *" options={filteredHands} selectedValue={handId} onChange={setHandId} />

        <h3 style={{ textTransform: "uppercase", marginTop: "30px" }}>Weapon Light</h3>
        <OptionSelector label="Select Light Model *" options={filteredLights} selectedValue={lightId} onChange={setLightId} />

        <hr style={{margin:"30px 0", borderTop:"1px solid #333"}}/>
        
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "15px" }}><h3 style={{ textTransform: "uppercase", margin: 0 }}>Color Options</h3></div>
        <ColorSelector options={nidhoggLvl2Data.colors} selectedValue={colorId} onChange={setColorId} />
        
        <hr style={{margin:"30px 0", borderTop:"1px solid #333"}}/>

        {/* --- MOUNTING OPTIONS (UPDATED WITH MEGA TOOLTIP) --- */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "15px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <h3 style={{ textTransform: "uppercase", margin: 0, fontSize: "14px", color: "#D5A678" }}>Mounting Platform</h3>
              <div className="tooltip-wrapper">
                  <span className="info-icon">i</span>
                  <div className="tooltip-content mega-tooltip">
                      {nidhoggLvl2Data.mounts.map(mount => (
                          <div key={mount.id} className="mount-info-row" style={{marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #555'}}>
                              <div className="mount-text">
                                  <h4 style={{marginTop: 0, marginBottom: '5px'}}>{mount.name}</h4>
                                  <p style={{margin: 0}}>{mount.description}</p>
                              </div>
                          </div>
                      ))}
                      <div style={{ textAlign: "center", marginTop: "10px" }}>
                          <a href="https://helpcenter.anrdesign.com/tactical-owb-nidhogg-accessories/" target="_blank" rel="noopener noreferrer" style={{ color: "#D5A678", textDecoration: "underline", fontWeight: "bold" }}>
                              Learn more about Nidhogg™ mounting options
                          </a>
                      </div>
                  </div>
              </div>
            </div>
            
             {(mountId === 'dropleg_qls' || mountId === 'dropleg_no_qls' || mountId === 'tac_belt_bolted') && (
                <span style={{ fontSize: "11px", color: "#D5A678", marginBottom: "12px", fontStyle: "italic" }}>
                    (See preview in image)
                </span>
            )}
        </div>
        <OptionSelector label="Select Mounting Option *" options={nidhoggLvl2Data.mounts} selectedValue={mountId} onChange={setMountId} />

        {/* --- CONDITIONAL MOUNT OPTIONS --- */}
        {(mountId === 'tac_belt_bolted' || mountId === 'dropleg_qls' || mountId === 'dropleg_no_qls') && (
            <div className="fade-in-section" style={{marginLeft: "15px", borderLeft: "2px solid #D5A678", paddingLeft: "15px", marginTop: "15px"}}>
                <SectionHeader title="Tactical Belt Mount Color" description="Select the color for your Tac Belt attachment." />
                <OptionSelector label="Select Color *" options={nidhoggLvl2Data.tac_belt_colors || []} selectedValue={tacBeltColor} onChange={setTacBeltColor} />
                
                <div style={{marginTop: "15px"}}></div>
                <SectionHeader title="Tactical Belt Mount Size" description="Choose the size of your belt loop. Short drops ride higher on the belt." imageUrl="/wp-content/uploads/anr-assets/images/info/tac_belt_sizes.jpg" />
                <OptionSelector label="Select Size *" options={nidhoggLvl2Data.tac_belt_sizes || []} selectedValue={tacBeltSize} onChange={setTacBeltSize} />
            </div>
        )}

        {(mountId === 'dropleg_qls' || mountId === 'dropleg_no_qls') && (
            <div className="fade-in-section" style={{marginLeft: "15px", borderLeft: "2px solid #D5A678", paddingLeft: "15px", marginTop: "15px"}}>
                <SectionHeader title="Fulcrum Color" description="Select the color for the pivoting fulcrum mechanism." imageUrl="/wp-content/uploads/anr-assets/images/info/fulcrum_colors.jpg" />
                <OptionSelector label="Select Fulcrum Color *" options={nidhoggLvl2Data.fulcrum_colors || []} selectedValue={fulcrumColor} onChange={setFulcrumColor} />
                
                <div style={{marginTop: "15px"}}></div>
                <SectionHeader title="Thigh Band Color" description="Select the color for the elastic thigh strap." imageUrl="/wp-content/uploads/anr-assets/images/info/thigh_bands.jpg" />
                <OptionSelector label="Select Thigh Band *" options={nidhoggLvl2Data.thigh_band_colors || []} selectedValue={thighBandColor} onChange={setThighBandColor} />
            </div>
        )}

        {/* --- MAG CARRIER SYSTEM --- */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "30px", marginBottom: "15px" }}>
           <h3 style={{ textTransform: "uppercase", margin: 0 }}>Add Matching Mag Carrier</h3>
           <div className="tooltip-wrapper">
              <span className="info-icon">i</span>
              <div className="tooltip-content">
                  Standard Pistol Magazine Carrier, matching your holster color.
              </div>
           </div>
        </div>
        <OptionSelector label="Add a Magazine Carrier *" options={nidhoggLvl2Data.mag_carriers} selectedValue={magId} onChange={setMagId} />

        {/* DYNAMIC MAG MOUNT */}
        {showMagMount && (
            <div className="fade-in-section" style={{marginLeft: "15px", borderLeft: "2px solid #D5A678", paddingLeft: "15px", marginTop: "15px"}}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                    <h4 style={{ color: "#D5A678", marginTop: "0", marginBottom: "0", fontSize: "14px", textTransform: "uppercase" }}>Select Mount for Magazine Carrier</h4>
                </div>
                <OptionSelector 
                    label="Mag Mount *" 
                    options={dynamicMagMounts} 
                    selectedValue={magMountId} 
                    onChange={setMagMountId} 
                />
            </div>
        )}

        <h3 style={{ textTransform: "uppercase", marginTop: "30px" }}>Add Breakthrough Products</h3>
        <p style={{ fontSize: "14px", color: "#666", marginBottom: "10px" }}>Optional firearm maintenance cleaning solvents.</p>
        <OptionSelector label="Optional firearm maintenance" options={nidhoggLvl2Data.breakthrough} selectedValue={breakthroughId} onChange={setBreakthroughId} />


        <hr style={{margin:"50px 0 20px 0", borderTop:"1px solid #333"}}/>

        {/* --- COLLAPSIBLE DESCRIPTION --- */}
        <CollapsibleSection title="Full Details & Specs">
            <div style={{ fontSize: "13px", lineHeight: "1.6", color: "#ccc", paddingBottom: "10px" }}>
                <p style={{marginBottom: "15px"}}>
                    <strong>Upgrade your competition setup</strong> with the ANR Design Nidhogg™ Level II Retention Holster and experience the difference.
                </p>
                <ul style={{ paddingLeft: "20px", marginTop: "5px", listStyleType: "disc" }}>
                    <li><strong>Optic and Suppressor Sights Ready:</strong> Accommodates most optics and suppressor-height sights up to 0.5″.</li>
                    <li><strong>Rigorous Testing:</strong> Withstands 400 lbs of hanging weight and 30,000 presses in various conditions without failure (Spec Sheets Available Upon Request).</li>
                    <li><strong>Competition Focused:</strong> Ideal for Tactical Games, PCSL, USPSA, and other competitive shooting sports.</li>
                </ul>

                <div style={{ borderLeft: "4px solid #D5A678", paddingLeft: "15px", marginBottom: "15px", marginTop: "15px" }}>
                    <p><strong>HOW IT WORKS:</strong> The mechanism must be pressed first before the pistol can be drawn from the holster. If you are to draw the pistol prematurely before pressing the button release, the holster will not release the firearm. The firearm must be then seated before pressing the push button release.</p>
                    <p style={{ color: "#ff6b6b", fontWeight: "bold", marginTop: "10px" }}>
                        WARNING: This holster is NOT compatible with gas pedals, thumb rests, Antimatter Gas Pedals, and Emissary Development Switches.
                    </p>
                </div>

                <p style={{marginBottom: "15px"}}>
                    <span style={{ color: "#ff6b6b", fontWeight: "bold" }}>DUTY USE DISCLAIMER:</span> We do not warrant the fitness or merchantability of this holster for Duty use. This holster was NOT designed for Duty use. It was specifically designed for the commercial market for avid shooters in mind. This holster is a fantastic addition to any battle belt or race belt configuration for Tactical Games, PCSL, USPSA or other competitive shooting sport league. It was designed to have lightning fast draw speed capabilities for competition purposes, while maintaining solid positive retention on the firearm when not in use.
                </p>

                <p style={{marginBottom: "15px"}}>
                    <strong>Bulk Purchasing:</strong> If you are interested in bulk purchasing, please use the <a href="https://www.anrkydexholsters.com/contact/" target="_blank" rel="noopener noreferrer" style={{color: "#D5A678"}}>Contact Form</a>. <br/>
                    For bulk military purchases, please visit: <a href="https://www.anrdesignsoldiersystems.com/contact/" target="_blank" rel="noopener noreferrer" style={{color: "#D5A678"}}>ANR Design Soldier Systems</a>.
                </p>

                <p style={{ fontStyle: "italic", borderTop: "1px solid #444", paddingTop: "10px", marginTop: "10px" }}>
                    <strong>Warranty:</strong> There is a Lifetime Warranty on the active-retention mechanism itself. Any issues with the mechanism will be repaired and replaced by ANR Design.
                </p>
            </div>
        </CollapsibleSection>

        {/* COLLAPSIBLE WARNINGS (Shows Mobile Disclaimers & Share) */}
        <div className="mobile-only-content">
            <CollapsibleSection title="Important Notes & Warnings">
                <DisclaimerBox mode="mobile" setShareModalOpen={setShareModalOpen} gunId={gunId} />
            </CollapsibleSection>
        </div>

      </div>

      <div className="preview-section">
        <div className="sticky-wrapper">

          <HolsterVisualizer 
            holsterType="nidhogg_l2" 
            gunData={selectedGun} 
            lightData={selectedLight}
            colorData={selectedColor} 
            mountData={visualizerMountData}
            tacBeltColor={tacBeltColor}
            fulcrumColor={fulcrumColor}
            thighBandColor={thighBandColor}
            isLightbearing="yes" 
            isLeftHanded={handId === 'left'}
            viewSide={viewSide} 
            systemImage={systemImageArray}
            hasSystemView={hasMountSystem}
          />

          {/* --- FRONT / REAR TOGGLE --- */}
          <div style={{ display: "flex", justifyContent: "center", marginTop: "15px", marginBottom: "15px" }}>
            <div style={{ display: "flex", backgroundColor: "#333", borderRadius: "4px", padding: "4px", gap: "5px" }}>
              <button onClick={() => setViewSide("front")} style={{ background: viewSide === "front" ? "#D5A678" : "transparent", color: viewSide === "front" ? "#000" : "#888", border: "none", padding: "6px 12px", borderRadius: "4px", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>FRONT</button>
              <button onClick={() => setViewSide("back")} style={{ background: viewSide === "back" ? "#D5A678" : "transparent", color: viewSide === "back" ? "#000" : "#888", border: "none", padding: "6px 12px", borderRadius: "4px", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>REAR</button>
            </div>
          </div>

          {/* --- MOVED LEAD TIME BOX --- */}
          {leadTimeInfo.type !== "PENDING" && (
            <div className="lead-time-box" style={{ 
                backgroundColor: leadTimeInfo.bg, 
                padding: "15px", 
                borderLeft: `4px solid ${leadTimeInfo.color}`, 
                marginBottom: "20px"
            }}>
               <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                   <div>
                       {leadTimeInfo.type && <strong style={{ color: leadTimeInfo.color, textTransform: "uppercase", fontSize: "16px" }}>{leadTimeInfo.type}</strong>}
                       <div style={{ marginTop: "5px", color: "#fff" }}>
                       Lead Time: <strong style={{ color: leadTimeInfo.color }}>{leadTimeInfo.timeline}</strong><br/>
                       Est. Ship Date: <strong>{leadTimeInfo.date}</strong>
                       </div>
                   </div>
               </div>
            </div>
          )}
          {leadTimeInfo.type === "PENDING" && (
            <div style={{ textAlign: "center", marginTop: "15px", color: "#666", fontSize: "12px", marginBottom: "15px" }}>Select options to see lead time</div>
          )}
          
          <div className="price-tag">
            {GLOBAL_DISCOUNT_PERCENT > 0 ? (
                <>
                <span style={{ textDecoration: "line-through", color: "#888", marginRight: "10px", fontSize: "0.8em" }}>${subtotal.toFixed(2)}</span>
                <span style={{ color: "#00ff00" }}>${total}</span>
                <div style={{ fontSize: "10px", color: "#00ff00", textTransform: "uppercase", marginTop: "5px" }}>
                    {SALE_NAME} ACTIVE
                </div>
                </>
            ) : (
                `Total: $${total}`
            )}
          </div>

          <DisclaimerBox mode="desktop" setShareModalOpen={setShareModalOpen} gunId={gunId} />

          <button className={`add-to-cart-btn ${isReady ? "pulse-active" : ""}`} disabled={!isReady} onClick={handleAddToCart}>{isReady ? "ADD TO CART" : "SELECT OPTIONS"}</button>
        </div>
      </div>
      
      {/* MOBILE BAR */}
      <div className="mobile-action-bar mobile-only-content">
        <div className="mobile-price-container"><span className="mobile-label">Estimated Total</span><span className="mobile-total">${total}</span></div>
        <button 
          className={`mobile-cart-btn ${isReady ? 'pulse-active' : ''}`} 
          disabled={!isReady} 
          onClick={handleAddToCart}
        >
           {isReady ? "ADD TO CART" : "INCOMPLETE"}
        </button>
      </div>

      <ShareModal isOpen={shareModalOpen} onClose={() => setShareModalOpen(false)} shareableURL={shareableURL} currentConfig={getCurrentConfig()} savedConfigs={savedConfigs} onSaveConfig={saveNamedConfig} onLoadConfig={loadConfig} onDeleteConfig={deleteNamedConfig} onCopyLink={copyToClipboard} copySuccess={copySuccess} gunName={selectedGun?.name} colorName={selectedColor?.name} />
    </div>
  );
}

export default NidhoggLvl2Builder;